/**
 * Cross-cutting primitives every backend module shares: the tenant context that
 * scopes all data access, id generation, subdomain rules, and the isolation
 * error type. No AWS or Node-only imports so this is safe to use from the
 * browser too (subdomain validation runs client-side in the wizard/dashboard).
 */

import type { Cadence } from '@nuff/types';

/**
 * The next issue date for a cadence, as an ISO 8601 string: `from` plus one
 * cadence interval (weekly = +7 days, biweekly = +14 days, monthly = +1 calendar
 * month). Pure and clock-injectable (`from` defaults to now) so the API can
 * schedule deterministically and it can be unit-tested. Month arithmetic uses UTC
 * calendar months and CLAMPS the day-of-month to the target month's length: a
 * naive `setUTCMonth(m + 1)` overflows a month-end (Jan 31 + 1 month becomes
 * "Feb 31", which JS rolls forward to Mar 3, skipping February entirely), so we
 * pin to the 1st before switching months and then clamp (Jan 31 -> Feb 28, or
 * Feb 29 in a leap year). Weekly/biweekly are plain day arithmetic and never
 * need clamping.
 */
export function nextIssueAt(cadence: Cadence, from: Date = new Date()): string {
  const next = new Date(from.getTime());
  switch (cadence) {
    case 'weekly':
      next.setUTCDate(next.getUTCDate() + 7);
      break;
    case 'biweekly':
      next.setUTCDate(next.getUTCDate() + 14);
      break;
    case 'monthly': {
      const day = next.getUTCDate();
      // Pin to the 1st first so switching the month can never overflow into the
      // month after the target (e.g. "Feb 31" rolling into March).
      next.setUTCDate(1);
      next.setUTCMonth(next.getUTCMonth() + 1);
      // The last day of the now-target month (day 0 of the following month).
      const lastDayOfTarget = new Date(
        Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0),
      ).getUTCDate();
      next.setUTCDate(Math.min(day, lastDayOfTarget));
      break;
    }
  }
  return next.toISOString();
}

/** The identity every data operation is scoped to. Built from the Cognito JWT. */
export interface TenantContext {
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
}

/** Thrown whenever an item's tenant doesn't match the caller's TenantContext. */
export class CrossTenantAccessError extends Error {
  constructor(message = 'Cross-tenant access denied') {
    super(message);
    this.name = 'CrossTenantAccessError';
  }
}

/**
 * Thrown when a globally-unique subdomain is already claimed by another tenant.
 * Distinct from a validation failure: the name is well-formed and allowed, it is
 * just taken. The API maps this to 409 Conflict.
 */
export class SubdomainTakenError extends Error {
  readonly subdomain: string;
  constructor(subdomain: string) {
    super(`The address "${subdomain}" is already taken.`);
    this.name = 'SubdomainTakenError';
    this.subdomain = subdomain;
  }
}

/** Prefixed, URL-safe id, e.g. createId('nl') -> 'nl_3f9a…'. */
export function createId(prefix: string): string {
  // Web Crypto is a global in both Node 19+ and browsers; type the bit we use
  // inline so this needs no DOM/Node ambient `Crypto` type.
  const webCrypto = globalThis as unknown as { crypto: { randomUUID: () => string } };
  const uuid = webCrypto.crypto.randomUUID().replace(/-/g, '');
  return `${prefix}_${uuid.slice(0, 24)}`;
}

// --- Subdomain rules --------------------------------------------------------

/**
 * Subdomains customers may NOT claim - our own properties and infra hostnames.
 * `platform`, `api`, `www` and friends are ours; keep this list generous.
 */
export const RESERVED_SUBDOMAINS: ReadonlySet<string> = new Set([
  'admin',
  'api',
  'app',
  'assets',
  'auth',
  'billing',
  'blog',
  'cdn',
  'dashboard',
  'dev',
  'docs',
  'email',
  'ftp',
  'help',
  'imap',
  'internal',
  'login',
  'mail',
  'mx',
  'ns',
  'platform',
  'root',
  'signup',
  'smtp',
  'staging',
  'static',
  'status',
  'support',
  'system',
  'test',
  'www',
]);

export type SubdomainResult = { ok: true; value: string } | { ok: false; reason: string };

/** Normalize + validate a customer subdomain. Min length 3, reserved words blocked. */
export function validateSubdomain(input: string): SubdomainResult {
  const value = input.trim().toLowerCase();
  if (value.length < 3) {
    return { ok: false, reason: 'Must be at least 3 characters.' };
  }
  if (value.length > 40) {
    return { ok: false, reason: 'Must be 40 characters or fewer.' };
  }
  if (!/^[a-z0-9-]+$/.test(value)) {
    return { ok: false, reason: 'Use only lowercase letters, numbers and hyphens.' };
  }
  if (value.startsWith('-') || value.endsWith('-')) {
    return { ok: false, reason: 'Cannot start or end with a hyphen.' };
  }
  if (value.includes('--')) {
    return { ok: false, reason: 'Cannot contain consecutive hyphens.' };
  }
  if (RESERVED_SUBDOMAINS.has(value)) {
    return { ok: false, reason: 'That subdomain is reserved.' };
  }
  return { ok: true, value };
}

// --- Tenant context from JWT claims ----------------------------------------

/**
 * Build a TenantContext from validated Cognito JWT claims. The tenantId comes
 * from the token (a `custom:tenantId` claim stamped by the pre-token-gen
 * Lambda) - never from a request body or header, so clients can't spoof it.
 */
export function tenantContextFromClaims(claims: Record<string, unknown>): TenantContext {
  const tenantId = claims['custom:tenantId'];
  const userId = claims.sub;
  if (typeof tenantId !== 'string' || tenantId.length === 0) {
    throw new CrossTenantAccessError('Token is missing a tenant');
  }
  if (typeof userId !== 'string' || userId.length === 0) {
    throw new CrossTenantAccessError('Token is missing a user');
  }
  // SECURITY: `roles` is NOT a trusted claim yet. The pre-token-gen Lambda stamps
  // `custom:tenantId` but does NOT stamp `custom:roles`, and the `roles` custom
  // attribute is user-mutable with self-sign-up enabled - so this value is
  // effectively caller-controlled. It is surfaced for plumbing only; do NOT gate
  // any authorization decision on `ctx.roles` until the trigger stamps it
  // (overriding user input), the same way it does for `custom:tenantId`.
  const rolesClaim = claims['custom:roles'];
  const roles =
    typeof rolesClaim === 'string' && rolesClaim.length > 0 ? rolesClaim.split(',') : [];
  return { tenantId, userId, roles };
}
